import React, {useEffect, useState} from 'react';
import {
    App,
    Button,
    ConfigProvider,
    Form,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Radio,
    Steps,
    Tag,
    theme,
    Tooltip,
    Typography,
} from 'antd';
import './ApiConnectionList.css';
import {
    ApiOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    GlobalOutlined,
    LoadingOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    RedoOutlined,
    StarFilled,
    StarOutlined,
} from '@ant-design/icons';
import {ApiConnectionDecryptedModel, ApiConnectionModel} from '../../types/apiConnection';
import {apiConnectionService} from '../../services/apiConnectionService';
import {useAuth} from '../../contexts/AuthContext';

const {Title} = Typography;
const {Password} = Input;
const {TextArea} = Input;

const PAGE_SIZE = 12;

type ExchangeOption = {
    id: string;
    name: string;
    color: string;
    logo: string;
    disabled: boolean;
    badgeText: string;
    hint: string;
};

// 交易所列表
const EXCHANGES: ExchangeOption[] = [
    {
        id: 'OKX',
        name: 'OKX',
        color: '#FFFFFF',
        logo: '/apikeys/okx.png',
        disabled: false,
        badgeText: '已支持',
        hint: '支持连接校验与交易能力接入'
    },
    {
        id: 'Bybit',
        name: 'Bybit',
        color: '#F7B600',
        logo: '/apikeys/bybit.svg',
        disabled: false,
        badgeText: '已支持',
        hint: '支持连接校验、行情查询与核心交易能力'
    },
    {
        id: 'Hyperliquid',
        name: 'Hyperliquid',
        color: '#7B61FF',
        logo: '/apikeys/hyperliquid.svg',
        disabled: true,
        badgeText: '即将上线',
        hint: '暂未开放，请先使用 OKX'
    },
];

const getExchangeOption = (exchangeId?: string) => EXCHANGES.find(item => item.id === exchangeId);

const isTradingReadyExchange = (exchangeId?: string) => exchangeId === 'OKX' || exchangeId === 'Bybit';

const ApiConnectionList: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const {user} = useAuth();
    const isAdmin = user?.roles?.includes('ADMIN') ?? false;
    const [connections, setConnections] = useState<ApiConnectionModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingConnection, setEditingConnection] = useState<ApiConnectionModel | null>(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedConnectionForDetail, setSelectedConnectionForDetail] = useState<ApiConnectionModel | null>(null);
    const [decryptedConnections, setDecryptedConnections] = useState<Map<number, ApiConnectionDecryptedModel>>(new Map());
    const [hideTimers, setHideTimers] = useState<Map<number, number>>(new Map());
    const [currentPage, setCurrentPage] = useState(1);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    // 独立的编辑弹窗状态
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState<ApiConnectionModel | null>(null);
    // 编辑表单数据
    const [editFormData, setEditFormData] = useState<{
        name: string;
        description: string;
        liveTrading: number;
    }>({
        name: '',
        description: '',
        liveTrading: 0,
    });

    // 分步表单状态
    const [createStep, setCreateStep] = useState(1);
    const [selectedExchange, setSelectedExchange] = useState<string>('');
    const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'failed'>('idle');
    const [testError, setTestError] = useState<string>('');
    const [testMessage, setTestMessage] = useState<string>('');
    const [apiFormData, setApiFormData] = useState<{
        apiKey: string;
        secretKey: string;
        passphrase: string;
        name: string;
        description: string;
        liveTrading: number;
    }>({
        apiKey: '',
        secretKey: '',
        passphrase: '',
        name: '',
        description: '',
        liveTrading: 0,
    });

    useEffect(() => {
        loadConnections();

        return () => {
            hideTimers.forEach(timer => clearTimeout(timer));
        };
    }, []);

    const loadConnections = async () => {
        setLoading(true);
        try {
            const data = await apiConnectionService.getAllConnections();
            setConnections(data);
        } catch (error) {
            messageApi.error('加载连接列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingConnection(null);
        form.resetFields();
        setCreateStep(1);
        setSelectedExchange('');
        setTestStatus('idle');
        setTestError('');
        setTestMessage('');
        setApiFormData({
            apiKey: '',
            secretKey: '',
            passphrase: '',
            name: '',
            description: '',
            liveTrading: 0,
        });
        setModalVisible(true);
    };

    const handleEdit = async (connection: ApiConnectionModel) => {
        setEditingRecord(connection);
        setEditFormData({
            name: connection.name || '',
            description: connection.description || '',
            liveTrading: connection.liveTrading || 0,
        });
        // 使用Form的setFieldsValue来回填数据
        editForm.setFieldsValue({
            name: connection.name || '',
            description: connection.description || '',
            liveTrading: connection.liveTrading || 0,
        });
        setEditModalVisible(true);
    };

    // 监听编辑表单字段变化
    const handleEditFormChange = (field: string, value: string | number) => {
        setEditFormData(prev => ({...prev, [field]: value}));
    };

    // 保存编辑
    const handleSaveEdit = async () => {
        if (!editingRecord?.id) return;

        try {
            await apiConnectionService.updateConnection(editingRecord.id, {
                name: editFormData.name,
                description: editFormData.description,
                liveTrading: editFormData.liveTrading,
            });
            messageApi.success('更新成功');
            setEditModalVisible(false);
            loadConnections();
        } catch (error) {
            messageApi.error('更新失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await apiConnectionService.deleteConnection(id);
            messageApi.success('删除成功');
            loadConnections();
        } catch (error) {
            messageApi.error('删除失败');
        }
    };

    // 重置连接：二次确认（清空历史数据不可恢复）后执行；存在运行中的永续仓位时后端拒绝并提示
    const handleReset = (connection: ApiConnectionModel) => {
        if (!connection.id) return;
        Modal.confirm({
            title: '确认重置该 API 连接？',
            icon: <ExclamationCircleOutlined/>,
            content: (
                <div style={{color: '#a0a0a0'}}>
                    <p>重置将清空该 key 的以下数据，并把「key 加入系统时间」重置为当前：</p>
                    <p>· 历史订单 · 仓位快照 · 权益历史与统计 · 余额快照</p>
                    <p style={{color: 'var(--pt-bear)'}}>
                        操作不可恢复；若该 key 存在运行中的永续仓位，将无法重置。
                    </p>
                </div>
            ),
            okText: '确认重置',
            cancelText: '取消',
            okButtonProps: {
                danger: true,
                style: {backgroundColor: 'var(--pt-bear)', borderColor: 'var(--pt-bear)'}
            },
            centered: true,
            className: 'dark-theme-modal',
            onOk: async () => {
                try {
                    await apiConnectionService.resetConnection(connection.id!);
                    messageApi.success('重置成功');
                    loadConnections();
                } catch (error: any) {
                    messageApi.error(error?.message || '重置失败');
                }
            },
        });
    };

    // 切换API连接状态（启用/禁用）
    const handleToggleStatus = async (connection: ApiConnectionModel) => {
        try {
            await apiConnectionService.toggleConnectionStatus(connection.id!);
            messageApi.success(connection.status === 'active' ? '已禁用' : '已启用');
            loadConnections();
        } catch (error) {
            messageApi.error('操作失败');
        }
    };

    const handleSetDefault = async (id: number) => {
        try {
            await apiConnectionService.setDefaultConnection(id);
            messageApi.success('设置成功');
            loadConnections();
        } catch (error) {
            messageApi.error('设置失败');
        }
    };

    const handleSetGlobalDisplay = async (id: number) => {
        try {
            await apiConnectionService.setGlobalDisplayConnection(id);
            messageApi.success('已设为全局展示');
            loadConnections();
        } catch (error: any) {
            messageApi.error(error.message || '设置失败');
        }
    };

    const handleCancelGlobalDisplay = async (id: number) => {
        try {
            await apiConnectionService.cancelGlobalDisplayConnection(id);
            messageApi.success('已取消全局展示');
            loadConnections();
        } catch (error: any) {
            messageApi.error(error.message || '取消失败');
        }
    };

    // 测试API连接
    const handleTestApi = async (id: number) => {
        try {
            const result = await apiConnectionService.testConnectionById(id);
            if (result.success) {
                messageApi.success(result.message || '连接校验通过');
            } else {
                messageApi.error(result.message || '连接校验失败');
            }
        } catch (error: any) {
            messageApi.error('测试失败：' + (error.message || '未知错误'));
        }
    };

    const handleViewDecrypted = async (id: number) => {
        try {
            const existingTimer = hideTimers.get(id);
            if (existingTimer) {
                clearTimeout(existingTimer);
            }

            const decryptedConnection = await apiConnectionService.getConnectionById(id);
            setDecryptedConnections(prev => new Map(prev).set(id, decryptedConnection));

            const timer = setTimeout(() => {
                handleHideDecrypted(id);
                messageApi.info('API信息已自动隐藏');
            }, 30000);

            setHideTimers(prev => new Map(prev).set(id, timer));
        } catch (error) {
            messageApi.error('获取解密信息失败');
        }
    };

    const handleHideDecrypted = (id: number) => {
        const timer = hideTimers.get(id);
        if (timer) {
            clearTimeout(timer);
        }

        setDecryptedConnections(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });

        setHideTimers(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });
    };

    const handleCardClick = (record: ApiConnectionModel) => {
        setSelectedConnectionForDetail(record);
        setDetailModalVisible(true);
    };

    const closeDetailModal = () => {
        setDetailModalVisible(false);
        if (selectedConnectionForDetail && selectedConnectionForDetail.id) {
            handleHideDecrypted(selectedConnectionForDetail.id);
        }
        setSelectedConnectionForDetail(null);
    };

    const copyToClipboard = (text: string, fieldName: string) => {
        navigator.clipboard.writeText(text).then(() => {
            messageApi.success(`${fieldName}已复制`);
        }).catch(() => {
            messageApi.error('复制失败');
        });
    };

    // 获取endpoint
    const getExchangeEndpoint = (exchange: string): string => {
        const endpoints: Record<string, string> = {
            'OKX': 'https://www.okx.com',
            'Bybit': 'https://api.bybit.com',
            'Hyperliquid': 'https://api.hyperliquid.xyz',
        };
        return endpoints[exchange] || '';
    };

    // 步骤1：选择交易所
    const handleSelectExchange = (exchangeId: string) => {
        setSelectedExchange(exchangeId);
    };

    // 步骤2：输入API信息
    const handleApiFormChange = (field: string, value: string) => {
        setApiFormData(prev => ({...prev, [field]: value}));
    };

    // 下一步
    const handleNextStep = () => {
        if (createStep === 1 && !selectedExchange) {
            messageApi.warning('请选择交易所');
            return;
        }
        if (createStep === 2) {
            if (!apiFormData.apiKey || !apiFormData.secretKey) {
                messageApi.warning('请输入API Key和Secret Key');
                return;
            }
        }
        setCreateStep(prev => prev + 1);
    };

    // 上一步
    const handlePrevStep = () => {
        setCreateStep(prev => prev - 1);
        setTestStatus('idle');
        setTestError('');
        setTestMessage('');
    };

    // 测试API连接
    const handleTestConnection = async () => {
        if (!selectedExchange || !apiFormData.apiKey || !apiFormData.secretKey) {
            messageApi.warning('请完善API信息');
            return;
        }

        setTestStatus('testing');
        setTestError('');
        setTestMessage('');

        try {
            const result = await apiConnectionService.testConnection({
                exchange: selectedExchange,
                apiKey: apiFormData.apiKey,
                secretKey: apiFormData.secretKey,
                passphrase: apiFormData.passphrase,
                endpoint: getExchangeEndpoint(selectedExchange),
            });

            if (result.success) {
                setTestStatus('success');
                setTestMessage(result.message || '连接校验通过');
                messageApi.success(result.message || '连接校验通过');
            } else {
                setTestStatus('failed');
                setTestError(result.message || '连接校验失败');
            }
        } catch (error: any) {
            setTestStatus('failed');
            setTestError(error.message || '测试失败');
        }
    };

    // 保存连接
    const handleSaveConnection = async () => {
        if (!selectedExchange || !apiFormData.apiKey || !apiFormData.secretKey) {
            messageApi.warning('请完善API信息');
            return;
        }

        try {
            await apiConnectionService.createConnection({
                exchange: selectedExchange,
                name: apiFormData.name || `${selectedExchange}连接`,
                apiKey: apiFormData.apiKey,
                secretKey: apiFormData.secretKey,
                passphrase: apiFormData.passphrase,
                endpoint: getExchangeEndpoint(selectedExchange),
                status: isTradingReadyExchange(selectedExchange) ? 'active' : 'inactive',
                description: apiFormData.description,
                liveTrading: apiFormData.liveTrading,
            });
            messageApi.success(isTradingReadyExchange(selectedExchange) ? '保存成功' : `${selectedExchange} 连接已保存，当前为预配置状态`);
            setModalVisible(false);
            loadConnections();
        } catch (error) {
            messageApi.error('保存失败');
        }
    };

    // 获取当前页的数据
    const getCurrentPageData = () => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return connections.slice(startIndex, endIndex);
    };

    // 渲染连接卡片
    const renderConnectionCard = (connection: ApiConnectionModel) => {
        const isInactive = connection.status !== 'active';
        const isTradingReady = isTradingReadyExchange(connection.exchange);
        const inactiveTooltip = isInactive
            ? (isTradingReady ? '该API已禁用' : `${connection.exchange} 当前仅支持预配置保存，不参与交易`)
            : '';
        const toggleTooltip = connection.status === 'active'
            ? '禁用API'
            : (isTradingReady ? '启用API' : `${connection.exchange} 暂不支持启用交易`);

        return (
            <Tooltip title={inactiveTooltip} placement="top">
                <div
                    key={connection.id}
                    className={`connection-card ${isInactive ? 'connection-card-inactive' : ''}`}
                    onClick={() => handleCardClick(connection)}
                >
                    <div className="connection-card-left">
                        <img
                            src={`/apikeys/${connection.exchange?.toLowerCase()}.png`}
                            alt={connection.exchange}
                            className="exchange-logo"
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                if (target.src.endsWith('.png')) {
                                    target.src = `/apikeys/${connection.exchange?.toLowerCase()}.svg`;
                                } else {
                                    target.style.display = 'none';
                                }
                            }}
                        />
                        <div
                            className={`connection-card-default-star ${connection.isDefault ? 'is-default' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!connection.isDefault) {
                                    if (!isTradingReady) {
                                        messageApi.warning(`${connection.exchange} 暂不支持设为默认交易连接`);
                                        return;
                                    }
                                    if (connection.status !== 'active') {
                                        messageApi.warning('请先启用该连接，再设为默认');
                                        return;
                                    }
                                    handleSetDefault(connection.id!);
                                }
                            }}
                        >
                            {connection.isDefault ? (
                                <StarFilled className="star-icon star-filled"/>
                            ) : (
                                <StarOutlined className="star-icon star-outline"/>
                            )}
                        </div>
                    </div>

                    <div className="connection-card-right">
                        <div className="connection-card-name">
                            <span
                                className={`connection-card-name-text ${isInactive ? 'connection-card-name-inactive' : ''}`}>
                                {connection.name || '未命名连接'}
                            </span>
                        </div>

                        <div className="connection-card-status">
                            {connection.liveTrading ? (
                                <Tag className="live-tag">实盘</Tag>
                            ) : (
                                <Tag className="simulated-tag">模拟</Tag>
                            )}
                        </div>

                        <div className="connection-card-description">
                            {connection.description || '暂无备注'}
                        </div>
                    </div>

                    <div className="connection-card-actions" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="编辑属性">
                            <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined/>}
                                onClick={() => handleEdit(connection)}
                            />
                        </Tooltip>
                        <Tooltip title={toggleTooltip}>
                            <Button
                                type="text"
                                size="small"
                                icon={connection.status === 'active' ? <PauseCircleOutlined/> : <PlayCircleOutlined/>}
                                onClick={() => {
                                    if (!isTradingReady && connection.status !== 'active') {
                                        messageApi.warning(`${connection.exchange} 当前仅支持预配置保存，暂不支持启用交易`);
                                        return;
                                    }
                                    handleToggleStatus(connection);
                                }}
                            />
                        </Tooltip>
                        {isAdmin && (
                            <Tooltip title={connection.isGlobalDisplay ? '取消全局展示' : '设为全局展示'}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={connection.isGlobalDisplay ?
                                        <StarFilled style={{color: 'var(--pt-primary)'}}/> :
                                        <GlobalOutlined/>}
                                    onClick={() => {
                                        if (connection.isGlobalDisplay) {
                                            handleCancelGlobalDisplay(connection.id!);
                                        } else {
                                            handleSetGlobalDisplay(connection.id!);
                                        }
                                    }}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="重置连接（清空该 key 的订单/仓位历史/资产统计并重新计时）">
                            <Button
                                type="text"
                                size="small"
                                icon={<RedoOutlined/>}
                                onClick={() => handleReset(connection)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="确定删除这个API吗？"
                            onConfirm={() => handleDelete(connection.id!)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Tooltip title="删除API">
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined/>}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </div>
                </div>
            </Tooltip>
        );
    };

    // 渲染步骤1：选择交易所
    const renderStep1 = () => (
        <div className="exchange-select-container">
            <div className="exchange-select-grid">
                {EXCHANGES.map(exchange => (
                    <Tooltip key={exchange.id} title={exchange.hint} placement="top">
                        <div
                            className={`exchange-card ${selectedExchange === exchange.id ? 'selected' : ''} ${exchange.disabled ? 'disabled' : ''}`}
                            onClick={() => !exchange.disabled && handleSelectExchange(exchange.id)}
                        >
                            <div className="exchange-logo-wrapper">
                                <img
                                    src={exchange.logo}
                                    alt={exchange.name}
                                    className="exchange-logo-img"
                                />
                            </div>
                            <Tag
                                color={exchange.disabled ? 'default' : (isTradingReadyExchange(exchange.id) ? 'green' : 'gold')}
                                style={{marginTop: 12}}
                            >
                                {exchange.badgeText}
                            </Tag>
                            <div style={{
                                marginTop: 8,
                                fontSize: 12,
                                color: 'var(--pt-text-secondary)',
                                textAlign: 'center',
                                lineHeight: 1.5
                            }}>
                                {exchange.hint}
                            </div>
                            {exchange.disabled &&
                                <div className="exchange-disabled-overlay"><span>即将上线</span></div>}
                        </div>
                    </Tooltip>
                ))}
            </div>
        </div>
    );

    // 渲染步骤2：输入API信息
    const renderStep2 = () => (
        <div className="api-form-container">
            <Form form={form} layout="vertical">
                <Form.Item label="连接名称" name="name">
                    <Input
                        placeholder="请输入连接名称（可选）"
                        value={apiFormData.name}
                        onChange={(e) => handleApiFormChange('name', e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="交易模式">
                    <Radio.Group
                        value={apiFormData.liveTrading}
                        onChange={(e) => setApiFormData(prev => ({...prev, liveTrading: e.target.value}))}
                    >
                        <Radio value={0}>
                            <span style={{color: 'var(--pt-bull)'}}>模拟交易</span>
                        </Radio>
                        <Radio value={1}>
                            <span style={{color: 'var(--pt-bear)'}}>实盘交易</span>
                        </Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item label="API Key" required>
                    <Password
                        placeholder="请输入API Key"
                        value={apiFormData.apiKey}
                        onChange={(e) => handleApiFormChange('apiKey', e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="Secret Key" required>
                    <Password
                        placeholder="请输入Secret Key"
                        value={apiFormData.secretKey}
                        onChange={(e) => handleApiFormChange('secretKey', e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="Passphrase">
                    <Password
                        placeholder="请输入Passphrase（如果需要）"
                        value={apiFormData.passphrase}
                        onChange={(e) => handleApiFormChange('passphrase', e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="备注" name="description">
                    <TextArea
                        rows={2}
                        placeholder="请输入备注（可选）"
                        value={apiFormData.description}
                        onChange={(e) => handleApiFormChange('description', e.target.value)}
                    />
                </Form.Item>

                {selectedExchange === 'Bybit' && (
                    <div style={{
                        marginTop: 12,
                        padding: '12px 14px',
                        borderRadius: 8,
                        border: '1px solid rgba(240, 180, 41, 0.35)',
                        background: 'rgba(240, 180, 41, 0.08)',
                        color: '#d9d9d9',
                        fontSize: 12,
                        lineHeight: 1.6
                    }}>
                        Bybit 已接入首期能力，当前支持连接校验、统一图表行情和核心下单链路。高级算法单能力暂未开放。
                    </div>
                )}
            </Form>
        </div>
    );

    // 渲染步骤3：测试并保存
    const renderStep3 = () => (
        <div className="test-container">
            <div className="test-status-box">
                {testStatus === 'idle' && (
                    <>
                        <ExclamationCircleOutlined style={{fontSize: 48, color: 'var(--pt-amber)', marginBottom: 16}}/>
                        <div
                            className="test-title">{isTradingReadyExchange(selectedExchange) ? '准备测试' : '准备校验'}</div>
                        <div className="test-desc">
                            {isTradingReadyExchange(selectedExchange)
                                ? '点击"测试连接"按钮验证API凭证并读取账户信息'
                                : '点击"校验连接"按钮检查必填信息，并确认该连接可作为预配置保存'}
                        </div>
                    </>
                )}
                {testStatus === 'testing' && (
                    <>
                        <LoadingOutlined style={{fontSize: 48, color: 'var(--pt-blue)', marginBottom: 16}}/>
                        <div
                            className="test-title">{isTradingReadyExchange(selectedExchange) ? '测试中...' : '校验中...'}</div>
                        <div className="test-desc">
                            {isTradingReadyExchange(selectedExchange) ? '正在验证API凭证' : '正在校验连接配置'}
                        </div>
                    </>
                )}
                {testStatus === 'success' && (
                    <>
                        <CheckCircleOutlined style={{fontSize: 48, color: 'var(--pt-bull)', marginBottom: 16}}/>
                        <div
                            className="test-title">{isTradingReadyExchange(selectedExchange) ? '测试成功' : '校验通过'}</div>
                        <div className="test-desc">{testMessage || '连接校验通过，点击保存完成创建'}</div>
                    </>
                )}
                {testStatus === 'failed' && (
                    <>
                        <ExclamationCircleOutlined style={{fontSize: 48, color: 'var(--pt-bear)', marginBottom: 16}}/>
                        <div className="test-title">测试失败</div>
                        <div className="test-error">{testError}</div>
                    </>
                )}
            </div>

            {testStatus !== 'success' && (
                <Button
                    type="primary"
                    size="large"
                    onClick={handleTestConnection}
                    loading={testStatus === 'testing'}
                    block
                >
                    {isTradingReadyExchange(selectedExchange) ? '测试连接' : '校验连接'}
                </Button>
            )}

            {testStatus === 'failed' && (
                <Button
                    size="large"
                    onClick={() => {
                        setTestStatus('idle');
                        setTestError('');
                        setTestMessage('');
                    }}
                    block
                    style={{marginTop: 12}}
                >
                    重新测试
                </Button>
            )}
        </div>
    );

    // 渲染创建Modal内容
    const renderCreateModalContent = () => {
        const selectedExchangeOption = getExchangeOption(selectedExchange);

        return (
            <div className="create-modal-content">
                <Steps
                    current={createStep - 1}
                    items={[
                        {title: '选择一个交易所'},
                        {title: '输入API信息'},
                        {title: '测试API'},
                    ]}
                    size="small"
                    style={{marginBottom: 24}}
                />

                {selectedExchangeOption && (
                    <div style={{
                        marginBottom: 16,
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        color: '#bfbfbf',
                        fontSize: 12
                    }}>
                        {selectedExchangeOption.name}：{selectedExchangeOption.hint}
                    </div>
                )}

                <div className="step-content">
                    {createStep === 1 && renderStep1()}
                    {createStep === 2 && renderStep2()}
                    {createStep === 3 && renderStep3()}
                </div>

                <div className="step-actions">
                    {createStep > 1 && (
                        <Button onClick={handlePrevStep}>上一步</Button>
                    )}
                    {createStep < 3 && (
                        <Button type="primary" onClick={handleNextStep}>下一步</Button>
                    )}
                    {createStep === 3 && testStatus === 'success' && (
                        <Button type="primary" onClick={handleSaveConnection}>保存</Button>
                    )}
                </div>
            </div>
        );
    };

    // 渲染编辑弹窗内容（只允许编辑名称、实盘/模拟、备注）
    const renderEditModalContent = () => (
        <Form form={editForm} layout="vertical">
            <Form.Item label="连接名称" name="name">
                <Input
                    placeholder="请输入连接名称"
                    value={editFormData.name}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                />
            </Form.Item>

            <Form.Item label="交易模式" name="liveTrading">
                <Radio.Group
                    value={editFormData.liveTrading}
                    onChange={(e) => handleEditFormChange('liveTrading', e.target.value)}
                >
                    <Radio value={0}>
                        <span style={{color: 'var(--pt-bull)'}}>模拟交易</span>
                    </Radio>
                    <Radio value={1}>
                        <span style={{color: 'var(--pt-bear)'}}>实盘交易</span>
                    </Radio>
                </Radio.Group>
            </Form.Item>

            <Form.Item label="备注" name="description">
                <TextArea
                    rows={3}
                    placeholder="请输入备注（可选）"
                    value={editFormData.description}
                    onChange={(e) => handleEditFormChange('description', e.target.value)}
                />
            </Form.Item>
        </Form>
    );

    const renderDetailModal = () => {
        if (!selectedConnectionForDetail) return null;

        const id = selectedConnectionForDetail.id!;
        const decryptedConnection = decryptedConnections.get(id);
        const isDecrypted = !!decryptedConnection;

        return (
            <ConfigProvider
                theme={{
                    algorithm: theme.darkAlgorithm,
                    token: {
                        colorBgContainer: 'var(--pt-bg-panel)',
                        colorBgElevated: 'var(--pt-bg-panel)',
                    }
                }}
            >
                <Modal
                    title={
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <span>API详情</span>
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <Tooltip title="测试API连接">
                                    <Button
                                        type="text"
                                        icon={<ApiOutlined/>}
                                        onClick={() => handleTestApi(id)}
                                        style={{
                                            backgroundColor: 'transparent',
                                            color: 'rgba(255, 255, 255, 0.85)',
                                            borderRadius: '4px',
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title={isDecrypted ? '隐藏密钥' : '显示密钥'}>
                                    <Button
                                        type="text"
                                        icon={isDecrypted ? <EyeInvisibleOutlined/> : <EyeOutlined/>}
                                        onClick={() => {
                                            if (isDecrypted) {
                                                handleHideDecrypted(id);
                                            } else {
                                                handleViewDecrypted(id);
                                            }
                                        }}
                                        style={{
                                            backgroundColor: isDecrypted ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                            color: isDecrypted ? 'var(--pt-primary)' : 'rgba(255, 255, 255, 0.85)',
                                            borderRadius: '4px',
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    />
                                </Tooltip>
                                <Button
                                    type="text"
                                    icon={<CloseOutlined/>}
                                    onClick={closeDetailModal}
                                    style={{
                                        color: 'rgba(255, 255, 255, 0.45)',
                                        borderRadius: '4px',
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginLeft: '4px'
                                    }}
                                />
                            </div>
                        </div>
                    }
                    open={detailModalVisible}
                    onCancel={closeDetailModal}
                    closable={false}
                    footer={[
                        <Button key="close" onClick={closeDetailModal} style={{marginTop: 8}}>
                            关闭
                        </Button>
                    ]}
                    width={600}
                >
                    <div style={{
                        background: 'rgba(7, 10, 18, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid rgb(64, 64, 64)'
                    }}>
                        <div style={{marginBottom: '12px'}}>
                            <span style={{color: 'rgb(160, 160, 160)'}}>名称：</span>
                            <span style={{color: '#ffffff'}}>{selectedConnectionForDetail.name || '-'}</span>
                        </div>
                        <div style={{marginBottom: '12px'}}>
                            <span style={{color: 'rgb(160, 160, 160)'}}>API Endpoint：</span>
                            <span style={{color: '#ffffff'}}>{selectedConnectionForDetail.endpoint}</span>
                        </div>
                        <div style={{marginBottom: '12px'}}>
                            <span style={{color: 'rgb(160, 160, 160)'}}>状态：</span>
                            <Tag color={selectedConnectionForDetail.status === 'active' ? 'green' : 'red'}>
                                {selectedConnectionForDetail.status === 'active' ? '活跃' : '禁用'}
                            </Tag>
                        </div>
                        <div style={{marginBottom: '12px'}}>
                            <span style={{color: 'rgb(160, 160, 160)'}}>描述：</span>
                            <span style={{color: '#ffffff'}}>{selectedConnectionForDetail.description || '-'}</span>
                        </div>
                    </div>

                    {isDecrypted && decryptedConnection && (
                        <div style={{
                            background: 'rgba(7, 10, 18, 0.3)',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid rgb(64, 64, 64)'
                        }}>
                            <div style={{marginBottom: '12px', fontWeight: 'bold', color: 'var(--pt-primary)'}}>
                                敏感信息
                            </div>
                            <div style={{marginBottom: '12px'}}>
                                <span style={{color: 'rgb(160, 160, 160)'}}>API Key：</span>
                                <span style={{fontFamily: 'monospace'}}>
                                    {decryptedConnection.apiKey ? decryptedConnection.apiKey.substring(0, 8) + '****' : '********************'}
                                </span>
                            </div>
                            <div style={{marginBottom: '12px'}}>
                                <span style={{color: 'rgb(160, 160, 160)'}}>Secret Key：</span>
                                <span style={{fontFamily: 'monospace'}}>
                                    {decryptedConnection.secretKey ? decryptedConnection.secretKey.substring(0, 8) + '****' : '********************'}
                                </span>
                            </div>
                            {decryptedConnection.passphrase && (
                                <div style={{marginBottom: '12px'}}>
                                    <span style={{color: 'rgb(160, 160, 160)'}}>Passphrase：</span>
                                    <span style={{fontFamily: 'monospace'}}>
                                        {decryptedConnection.passphrase.substring(0, 8) + '****'}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {isDecrypted && (
                        <div style={{marginTop: '16px', color: 'var(--pt-amber)', fontSize: '12px'}}>
                            安全提示：敏感信息已掩码展示。系统将在30秒后自动隐藏敏感信息。
                        </div>
                    )}
                </Modal>
            </ConfigProvider>
        );
    };

    return (
        <div style={{padding: '24px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24}}>
                <Title level={4} style={{color: '#ffffff', margin: 0}}></Title>
                <Button type="primary" icon={<PlusOutlined/>} onClick={handleCreate}>

                </Button>
            </div>

            {/* 安全提示 */}
            <div style={{
                background: 'var(--pt-bull-dim)',
                border: '1px solid rgba(0,200,150,0.25)',
                borderRadius: '12px',
                padding: '1rem 1.25rem',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
                marginBottom: '20px'
            }}>
                <span style={{color: 'var(--pt-bull)', fontSize: '1.65rem', flexShrink: 0}}>🔒</span>
                <div style={{fontSize: '1.28rem', color: 'var(--text)', lineHeight: '1.65'}}>
                    <strong style={{color: 'var(--pt-bull)'}}>安全承诺：</strong>API 密钥仅存储于加密数据库（AES-256），我们只申请<strong>交易权限</strong>，绝不申请提现权限。您的资金无法通过
                    API 被转移。建议专Key专用，请勿将配置在本平台的Key应用在其他工具/服务中。
                </div>
            </div>

            {connections.length > 0 ? (
                <>
                    <div className="connection-grid">
                        {getCurrentPageData().map(renderConnectionCard)}
                    </div>

                    {connections.length > PAGE_SIZE && (
                        <div className="connection-pagination">
                            <Pagination
                                current={currentPage}
                                pageSize={PAGE_SIZE}
                                total={connections.length}
                                onChange={(page) => setCurrentPage(page)}
                                showSizeChanger={false}
                                showQuickJumper={false}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className="connection-empty">
                    <GlobalOutlined style={{fontSize: 48, marginBottom: 16, color: 'var(--pt-text-secondary)'}}/>
                    <p>暂无API，请点击"+"添加</p>
                </div>
            )}

            {renderDetailModal()}

            <Modal
                title="添加新的API"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
                closable={true}
                onClose={() => setModalVisible(false)}
            >
                {renderCreateModalContent()}
            </Modal>

            <Modal
                title="编辑API属性"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                width={500}
                closable={true}
                footer={[
                    <Button key="cancel" onClick={() => setEditModalVisible(false)}>
                        取消
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveEdit}>
                        保存
                    </Button>,
                ]}
            >
                {renderEditModalContent()}
            </Modal>
        </div>
    );
};

export default ApiConnectionList;
